// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import { useEffect, useState } from 'react';
import { subMonths, startOfDay } from 'date-fns';
import { getAggregatedDeliveryBales, getAggregatedSupplierDeliveryBales } from '../utils/ObservationAggregationUtils';

/**
 * Calculate the deviation of brown paper content from a benchmark (industry or supplier)
 *
 * @param {number} brownPaperAverage - The brown paper average for the delivery (0-1 scale)
 * @param {number} benchmarkAverage - The benchmark average to compare against (0-1 scale)
 * @returns {Object|null} - Object with { difference, isAboveMean } or null if calculation not possible
 */
export function calculateBrownPaperDeviation(brownPaperAverage, benchmarkAverage) {
  if (!brownPaperAverage || benchmarkAverage === null || benchmarkAverage === undefined) {
    return null;
  }

  const benchmarkPercentage = Math.round(benchmarkAverage * 100);
  const actualPercentage = Math.round(brownPaperAverage * 100);
  const difference = actualPercentage - benchmarkPercentage;
  const isAboveMean = difference >= 0;

  return {
    difference,
    isAboveMean,
    benchmarkPercentage,
    actualPercentage,
  };
}

/**
 * Extract brown paper content benchmarks by material from aggregated data
 *
 * @param {Object} aggregatedData - The aggregated delivery bales data
 * @returns {Object} - Dictionary with material keys and brown paper content stats
 */
function extractBrownPaperByMaterial(aggregatedData) {
  if (!aggregatedData?.reduced_data?.data_per_material) return {};

  const result = {};
  for (const [materialKey, materialData] of Object.entries(aggregatedData.reduced_data.data_per_material)) {
    const brownPaperContent = materialData?.data_per_labelset?.brown_paper_content;
    if (brownPaperContent) {
      result[materialKey] = {
        average: brownPaperContent.average ?? null,
        min: brownPaperContent.min ?? null,
        max: brownPaperContent.max ?? null,
        std: brownPaperContent.std ?? null,
      };
    }
  }
  return result;
}

/**
 * Custom hook to load industry benchmark data for brown paper content
 *
 * @param {string} clientId - The client ID
 * @returns {Object} - Object with { industryAverages, loading }
 *   where industryAverages is a dictionary keyed by material
 */
export function useIndustryBrownPaperBenchmarks(firestore, clientId, globalLabelsets) {
  const [industryAverages, setIndustryAverages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchIndustryBenchmarks() {
      if (!clientId) {
        setLoading(false);
        return;
      }

      setLoading(true);

      const endDate = startOfDay(new Date());
      const startDate = subMonths(endDate, 3);
      const timeIntervalMins = 24 * 60;

      const aggregatedDeliveryBalesData = await getAggregatedDeliveryBales(
        firestore,
        clientId,
        startDate,
        endDate,
        timeIntervalMins,
        globalLabelsets,
      );
      const industryData = extractBrownPaperByMaterial(aggregatedDeliveryBalesData);
      setIndustryAverages(industryData);
      setLoading(false);
    }

    fetchIndustryBenchmarks();
  }, [clientId]);

  return { industryAverages, loading };
}

/**
 * Custom hook to load supplier benchmark data for brown paper content
 *
 * @param {string} clientId - The client ID
 * @param {string} supplierId - The supplier ID
 * @returns {Object} - Object with { supplierAverages, loading }
 *   where supplierAverages is a dictionary keyed by material
 */
export function useSupplierBrownPaperBenchmarks(firestore, clientId, supplierId, globalLabelsets) {
  const [supplierAverages, setSupplierAverages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchSupplierBenchmarks() {
      if (!clientId || !supplierId) {
        setLoading(false);
        setSupplierAverages({});
        return;
      }

      setLoading(true);

      const endDate = startOfDay(new Date());
      const startDate = subMonths(endDate, 3);
      const timeIntervalMins = 24 * 60;

      const aggregatedSupplierDeliveryBalesData = await getAggregatedSupplierDeliveryBales(
        firestore,
        clientId,
        supplierId,
        startDate,
        endDate,
        timeIntervalMins,
        globalLabelsets,
      );
      const supplierData = extractBrownPaperByMaterial(aggregatedSupplierDeliveryBalesData);
      setSupplierAverages(supplierData);
      setLoading(false);
    }

    fetchSupplierBenchmarks();
  }, [clientId, supplierId]);

  return { supplierAverages, loading };
}
